import { useEffect, useMemo, useState } from 'react'
import useSWRInfinite from 'swr/infinite'
import axios from 'axios'
import Cookies from 'js-cookie'
import { apiURI, constants } from 'src/lib/utils/constants'
import { staticOptions } from 'src/lib/core/api-client'
import { normalizeReports } from 'src/components/Reports/parser'
import { BackendReport } from 'src/components/Reports/types'

const ROWS_PER_PAGE = 10
const POLL_MS = 5000
const FIRST_RUN_POLL_WINDOW_MS = 30 * 60 * 1000

interface ReportsPageResponse {
  reportList?: BackendReport[]
  nextOffset?: number | null
}

const fetchPage = async (url: string): Promise<ReportsPageResponse> => {
  const response = await axios.get(url, {
    baseURL: apiURI.CHECKOUT_NODE,
    headers: { Authorization: Cookies.get(constants.AUTH_COOKIE_CLIENT) ?? '' }
  })
  return response.data
}

const shouldKeepPolling = (report: BackendReport): boolean => {
  const status = report.runs?.[0]?.status
  if (status === 'PROCESSING') return true
  if (report.frequencyType === 'ONCE' && status === 'SCHEDULED') return true
  if (report.frequencyType === 'RECURRING' && (report.runs?.length ?? 0) === 0 && report.runStartAt) {
    const due = new Date(report.runStartAt).getTime()
    if (!Number.isNaN(due) && Math.abs(Date.now() - due) < FIRST_RUN_POLL_WINDOW_MS) return true
  }
  return false
}

// Server-side ordering: DESC = newest first (default), ASC = oldest first.
export type ReportsSortOrder = 'DESC' | 'ASC'

export const useReportsList = (
  type: string,
  sortOrder: ReportsSortOrder = 'DESC',
  search = '',
  frequency = ''
) => {
  // Debounce the search text so we don't refetch on every keystroke (matches useUpsellsList).
  const [debouncedSearch, setDebouncedSearch] = useState(search)
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(search), 400)
    return () => clearTimeout(handler)
  }, [search])

  const getKey = (pageIndex: number, previousPageData: ReportsPageResponse | null) => {
    if (previousPageData && previousPageData.nextOffset == null) return null
    const offset = pageIndex === 0 ? 0 : previousPageData?.nextOffset ?? 0
    const trimmed = debouncedSearch.trim()
    const searchParam = trimmed ? `&search=${encodeURIComponent(trimmed)}` : ''
    const frequencyParam = frequency ? `&frequency=${frequency}` : ''
    return `/dashboard/report/list?type=${type}&limit=${ROWS_PER_PAGE}&offset=${offset}&sortOrder=${sortOrder}${searchParam}${frequencyParam}`
  }

  const { data, size, setSize, isValidating, mutate } = useSWRInfinite<ReportsPageResponse>(
    getKey,
    fetchPage,
    {
      ...staticOptions,
      revalidateFirstPage: true,
      // Poll only while something is expected to change; returns 0 (off) once everything has settled.
      refreshInterval: (pages?: ReportsPageResponse[]) =>
        (pages ?? []).some(page => (page?.reportList ?? []).some(shouldKeepPolling)) ? POLL_MS : 0
    }
  )

  useEffect(() => {
    setSize(1)
  }, [type, sortOrder, debouncedSearch, frequency, setSize])

  const reports = useMemo(
    () => normalizeReports(data?.flatMap(page => page?.reportList ?? []) ?? []),
    [data]
  )

  const lastPage = data?.[data.length - 1]
  const hasMore = !!lastPage && lastPage.nextOffset != null
  const isInitialLoading = isValidating && size === 1 && reports.length === 0
  const isLoadingMore = isValidating && size > 1

  return {
    reports,
    hasMore,
    size,
    setSize,
    isValidating,
    isInitialLoading,
    isLoadingMore,
    mutate
  }
}
